import React from "react";

export type StatCardAccent =
  | "brand"
  | "success"
  | "warning"
  | "danger"
  | "neutral";

export interface StatCardProps {
  label: string;
  value: React.ReactNode;
  unit?: string;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  accent?: StatCardAccent;
  progress?: number;
  selected?: boolean;
  size?: "default" | "compact";
  onClick?: () => void;
  style?: React.CSSProperties;
}

const ACCENT_COLOR: Record<StatCardAccent, string> = {
  brand: "var(--semi-color-primary)",
  success: "var(--semi-color-success)",
  warning: "var(--semi-color-warning)",
  danger: "var(--semi-color-danger)",
  neutral: "var(--semi-color-text-2)",
};

export function StatCard(props: StatCardProps) {
  const {
    label,
    value,
    unit,
    hint,
    icon,
    accent = "neutral",
    progress,
    selected = false,
    size = "default",
    onClick,
    style,
  } = props;

  const accentColor = ACCENT_COLOR[accent];
  const interactive = typeof onClick === "function";

  if (size === "compact") {
    return (
      <div
        onClick={onClick}
        style={{
          padding: "12px 14px",
          borderRadius: 8,
          background: "transparent",
          cursor: interactive ? "pointer" : "default",
          ...(style ?? {}),
        }}
      >
        <div style={{ fontSize: 12, color: "var(--semi-color-text-2)", marginBottom: 4 }}>
          {label}
        </div>
        <div style={{ fontSize: 20, fontWeight: 600, color: "var(--semi-color-text-0)" }}>
          {value}
        </div>
      </div>
    );
  }

  const progressBar =
    typeof progress === "number" && !isNaN(progress) ? (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
        <div
          style={{
            flex: 1,
            height: 6,
            borderRadius: 3,
            background: "var(--semi-color-fill-1)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${Math.min(100, Math.max(0, progress))}%`,
              height: "100%",
              borderRadius: 3,
              background: accentColor,
              transition: "width .3s",
            }}
          />
        </div>
        <span style={{ fontSize: 12, color: "var(--semi-color-text-2)", fontVariantNumeric: "tabular-nums" }}>
          {progress.toFixed(0)}%
        </span>
      </div>
    ) : null;

  return (
    <div
      onClick={onClick}
      style={{
        position: "relative",
        padding: 16,
        borderRadius: 8,
        background: "var(--semi-color-bg-1)",
        border: `1px solid ${
          selected ? "var(--semi-color-primary)" : "var(--semi-color-border)"
        }`,
        boxShadow: selected ? "var(--semi-shadow-elevated)" : "none",
        cursor: interactive ? "pointer" : "default",
        transition: "border-color .2s, box-shadow .2s",
        overflow: "hidden",
        ...(style ?? {}),
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: accentColor,
        }}
      />
      {icon && (
        <div
          style={{
            position: "absolute",
            right: 14,
            top: 14,
            color: "var(--semi-color-text-2)",
            fontSize: 16,
          }}
        >
          {icon}
        </div>
      )}
      <div style={{ fontSize: 13, color: "var(--semi-color-text-2)", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
        <span
          style={{
            fontSize: 30,
            fontWeight: 600,
            color: "var(--semi-color-text-0)",
            lineHeight: 1.1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
        </span>
        {unit && (
          <span style={{ fontSize: 14, color: "var(--semi-color-text-2)" }}>{unit}</span>
        )}
      </div>
      {progressBar}
      {hint && (
        <div style={{ fontSize: 12, color: "var(--semi-color-text-2)", marginTop: 6 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export default StatCard;
